import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { addMonths, startOfMonth } from 'date-fns';
import {
  useResetLeaderboard,
  useResetSchedule,
  useUpdateResetSchedule,
} from '@/services/gamification';
import GradientCard from './GradientCard';
import MonthCalendar from './MonthCalendar';
import ClockTimePicker, { TimeValue } from './ClockTimePicker';

const DEFAULT_TIME: TimeValue = { hour: 9, minute: 0, meridiem: 'AM' };

/** TimeValue (12h) -> "HH:MM" 24h, the shape the backend stores. */
const toHHMM = (t: TimeValue): string => {
  const h = t.meridiem === 'PM' ? (t.hour % 12) + 12 : t.hour % 12;
  return `${String(h).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`;
};

/** "HH:MM" 24h -> TimeValue (12h) for the clock picker. */
const fromHHMM = (value: string | null): TimeValue => {
  if (!value) return DEFAULT_TIME;
  const [hh, mm] = value.split(':').map(Number);
  return {
    hour: hh % 12 === 0 ? 12 : hh % 12,
    minute: Number.isFinite(mm) ? mm : 0,
    meridiem: hh >= 12 ? 'PM' : 'AM',
  };
};

/** A day-of-month -> a Date in the current month (clamped) for the calendar. */
const dateForDay = (day: number): Date => {
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return new Date(now.getFullYear(), now.getMonth(), Math.min(Math.max(day, 1), daysInMonth));
};

const GamificationScheduleCard: React.FC = () => {
  const resetNow = useResetLeaderboard();

  // The schedule says WHEN the season rolls over; this starts one now. The
  // endpoint has always existed, nothing called it (Aleix, 2026-09-09).
  const handleResetNow = async () => {
    if (
      !window.confirm(
        'Start a new season now? Everyone returns to zero points. The current standings are '
          + 'archived and stay available in the season reports.'
      )
    ) {
      return;
    }
    try {
      await resetNow.mutateAsync(undefined);
      toast.success('New season started — the leaderboard is back to zero.');
    } catch {
      toast.error('Could not start a new season. Please try again.');
    }
  };

  const { data: schedule } = useResetSchedule();
  const updateSchedule = useUpdateResetSchedule();

  const [date, setDate] = useState<Date>(() => startOfMonth(addMonths(new Date(), 1)));
  // The canonical day-of-month, held separately from the calendar `Date` so a
  // display-clamp (e.g. day 31 shown as 28 in February) never overwrites the
  // stored value on re-save. Only an actual calendar pick updates it.
  const [dayOfMonth, setDayOfMonth] = useState<number>(() => date.getDate());
  const [time, setTime] = useState<TimeValue>(DEFAULT_TIME);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate the controls once from the persisted schedule.
  useEffect(() => {
    if (!schedule || hydrated) return;
    if (schedule.reset_day_of_month) {
      setDayOfMonth(schedule.reset_day_of_month);
      setDate(dateForDay(schedule.reset_day_of_month));
    }
    if (schedule.reset_time) setTime(fromHHMM(schedule.reset_time));
    setHydrated(true);
  }, [schedule, hydrated]);

  const handleDateChange = (d: Date) => {
    setDate(d);
    setDayOfMonth(d.getDate());
  };

  const handleSubmit = async () => {
    try {
      await updateSchedule.mutateAsync({
        reset_day_of_month: dayOfMonth,
        reset_time: toHHMM(time),
        reset_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        recurrence: 'monthly',
      });
      toast.success('Leaderboard reset schedule saved');
    } catch {
      toast.error('Could not save the schedule. Please try again.');
    }
  };

  // next_reset_at arrives as naive UTC — JS would read it as local time and
  // show the raw UTC clock. Anchor it to UTC, render it in the schedule's
  // own timezone so it always agrees with the summary sentence.
  const nextReset = schedule?.next_reset_at
    ? new Date(
        /Z$|[+-]\d\d:?\d\d$/.test(schedule.next_reset_at)
          ? schedule.next_reset_at
          : `${schedule.next_reset_at}Z`
      ).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: schedule.reset_timezone ?? undefined,
      })
    : null;

  // The saved schedule in one unambiguous sentence — day, time, and the
  // timezone it's anchored to (the pickers below are just editors).
  const summary =
    schedule?.reset_day_of_month && schedule?.reset_time
      ? `Resets on day ${schedule.reset_day_of_month} of every month at ` +
        `${schedule.reset_time}${schedule.reset_timezone ? ` (${schedule.reset_timezone})` : ''}.`
      : null;

  return (
    <GradientCard className="rounded-3xl p-6">
      <h2 className="text-[1.4rem] font-medium leading-snug text-primary-500 dark:text-primary-300">
        Leaderboard Reset Schedule
      </h2>
      <p className="mt-2 max-w-2xl body-small text-text-secondary">
        Choose the day and time the Leaderboard resets each month. On reset, each
        learner&rsquo;s points are archived and then returned to zero. Also, the reset
        date is the last day for learners to claim their prizes!
      </p>

      {summary && (
        <p className="mt-4 text-[15px] font-semibold text-text-primary" data-testid="reset-summary">
          {summary}
        </p>
      )}

      {/* Three columns, edge to edge: calendar | clock | next reset + save */}
      <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-stretch lg:gap-6">
        <div className="rounded-3xl border border-border-primary bg-surface-elevated p-4 shadow-lg sm:p-5 lg:flex-1">
          <MonthCalendar value={date} onChange={handleDateChange} />
        </div>
        <div className="flex items-center rounded-3xl border border-border-primary bg-surface-elevated p-4 shadow-lg sm:p-5 lg:w-[380px] lg:shrink-0">
          <ClockTimePicker value={time} onChange={setTime} />
        </div>
        {/* Third column: floating text + save, deliberately unframed. The
            vertical padding mirrors the picker cards' so the three overline
            titles sit on the same line. */}
        <div className="flex flex-col py-4 sm:py-5 lg:w-[220px] lg:shrink-0">
          <p className="text-xs font-bold uppercase tracking-wider text-text-secondary">
            Next reset
          </p>
          {/* Centered with the pickers' controls, like the grid and the dial */}
          <div className="flex flex-1 flex-col justify-center gap-4 pt-2">
            {nextReset ? (
              <p className="text-base font-semibold leading-snug text-text-primary">{nextReset}</p>
            ) : (
              // Unsaved state must be unmistakable: the pickers show default
              // suggestions that look identical to a saved schedule.
              <p
                role="status"
                className="body-small font-medium leading-snug text-warning-600 dark:text-warning-400"
              >
                Nothing scheduled yet — the calendar and clock are only suggestions until you press
                Save.
              </p>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={updateSchedule.isPending}
              className="btn-primary w-full active:scale-[0.97]"
            >
              {updateSchedule.isPending ? 'Saving…' : 'Save'}
            </button>

            <button
              type="button"
              onClick={handleResetNow}
              disabled={resetNow.isPending}
              className="w-full rounded-full border border-border-primary/50 px-4 py-2 text-sm font-bold text-text-primary transition-[background-color,transform] duration-150 hover:bg-text-primary/[0.06] focus-ring active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resetNow.isPending ? 'Starting…' : 'Start a new season now'}
            </button>
            <p className="body-small text-center text-text-tertiary">
              Archives the current standings and returns everyone to zero.
            </p>
          </div>
        </div>
      </div>
    </GradientCard>
  );
};

export default GamificationScheduleCard;
